import { useMount, useUnmount } from 'ahooks';
import { Spin } from 'antd';
import { useGetConversationInfo, useMakeConversationCache } from 'hooks/business/conversation';
import {
  conversationAtomFamily,
  useAddActiveConversation,
  useRemoveActiveConversation,
} from 'hooks/business/lruConversation';
import useModal from 'hooks/useModal';
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAtomValue } from 'jotai';
import { getFacebookBuyer, getJolectBuyer } from 'services/api/conversation';
import createNamedContext from 'utils/createNamedContext';
import lastUsedConversation from 'utils/lastUsedConversation';
import { useIsLightChatLayout } from 'hooks/useChatLayoutType';
import { noop } from 'lodash-es';

const usePhraseModal = () => useModal<JSX.Element>();

interface ChatConversationContextValue {
  currentConversationId: Conversation.ConversationId;
  currentConversation: Conversation.Conversation;
  phraseModal?: ReturnType<typeof usePhraseModal>;
  facebookBuyer: Conversation.FacebookBuyer | undefined;
  setFacebookBuyer: Dispatch<SetStateAction<Conversation.FacebookBuyer | undefined>>;
  jolectBuyer: Conversation.JolectBuyer | undefined;
  setJolectBuyer: Dispatch<SetStateAction<Conversation.JolectBuyer | undefined>>;
}

export const ChatConversationContext = createNamedContext<ChatConversationContextValue | null>(
  'ChatConversationContext',
  null,
);

export function useChatConversationContext() {
  const value = useContext(ChatConversationContext);

  if (value === null) {
    throw new Error(
      'useChatConversationContext hook should be used in a context of ChatConversationProvider component',
    );
  }

  return value;
}

interface ChatConversationProviderProps {
  conversationId: Conversation.ConversationId;
}

const ChatConversationContextProvider = (
  props: PropsWithChildren<{ conversation: Conversation.Conversation }>,
) => {
  const { conversation, ...reset } = props;
  const phraseModal = usePhraseModal();
  const isLightChatLayout = useIsLightChatLayout();

  const [facebookBuyer, setFacebookBuyer] = useState<Conversation.FacebookBuyer>();
  const [jolectBuyer, setJolectBuyer] = useState<Conversation.JolectBuyer>();
  useEffect(() => {
    if (conversation.channel === 'FACEBOOK') {
      getFacebookBuyer(conversation.externalBuyerId).then(setFacebookBuyer).catch(noop);
      return () => setFacebookBuyer(undefined);
    }
    if (conversation.channel === 'JOLECT') {
      getJolectBuyer(conversation.externalBuyerId).then(setJolectBuyer).catch(noop);
      return () => setJolectBuyer(undefined);
    }
  }, [conversation.externalBuyerId, conversation.channel]);

  return (
    <ChatConversationContext.Provider
      value={{
        currentConversation: conversation,
        currentConversationId: conversation.conversationId,
        phraseModal: isLightChatLayout ? undefined : phraseModal,
        facebookBuyer,
        setFacebookBuyer,
        jolectBuyer,
        setJolectBuyer,
      }}
      {...reset}
    />
  );
};

export const ChatConversationProvider = (
  props: PropsWithChildren<ChatConversationProviderProps>,
) => {
  const { conversationId, ...otherProps } = props;

  // 使用 atomFamily 响应式获取会话数据，只在该会话变化时重渲染
  const currentConversation = useAtomValue(conversationAtomFamily(conversationId));

  const makeConversationCache = useMakeConversationCache('set');
  const getConversationInfo = useGetConversationInfo();

  const addActiveConversation = useAddActiveConversation();
  const removeActiveConversation = useRemoveActiveConversation();

  useMount(() => {
    // 标记为活跃会话，防止被 LRU 淘汰
    addActiveConversation(conversationId);

    if (!currentConversation) {
      getConversationInfo(conversationId)
        .then((conversation) => {
          lastUsedConversation.push(conversationId);
          makeConversationCache(conversation);
        })
        .catch(noop);
    } else {
      lastUsedConversation.push(conversationId);
    }
  });

  useUnmount(() => {
    // 移除活跃状态
    removeActiveConversation(conversationId);
  });

  return currentConversation ? (
    <ChatConversationContextProvider conversation={currentConversation} {...otherProps} />
  ) : (
    <div className="flex-1 flex items-center justify-center">
      <Spin />
    </div>
  );
};
